import { Arrow, Content, Portal, Provider, Root, Trigger } from '@radix-ui/react-tooltip';
import { ComponentPropsWithoutRef, FC, useEffect, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import Icon from 'molecules/Icon';

export interface HelperToolTipProps extends ComponentPropsWithoutRef<'div'> {
  label?: string;
  description?: string;
  delayDuration?: number;
  autoCloseDuration?: number;
}

interface TooltipProps extends HelperToolTipProps {
  children?: React.ReactNode;
}

const Tooltip: FC<TooltipProps> = ({
  children,
  label,
  description,
  className,
  delayDuration = 300,
  autoCloseDuration = 4000,
}) => {
  const [open, setOpen] = useState(false);
  const [controlledMode, setControlledMode] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const openTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setOpen(true);

    if (autoCloseDuration > 0) {
      timeoutRef.current = setTimeout(() => {
        setOpen(false);
        setControlledMode(false);
      }, autoCloseDuration);
    }
  };

  const closeTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setOpen(false);
    setControlledMode(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    setControlledMode(true);
    if (open) {
      closeTooltip();
    } else {
      openTooltip();
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!controlledMode) {
      setOpen(newOpen);
    }
  };

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  return (
    <Provider>
      <Root delayDuration={delayDuration} open={open} onOpenChange={handleOpenChange}>
        <Trigger asChild>
          <span
            onTouchStart={handleTouchStart}
            className={twJoin(children && 'border-b border-b-gray-300 border-dashed', 'cursor-pointer', className)}
          >
            {children || <Icon icon="help-circle" iconColor="text-gray-400" size={16} />}
          </span>
        </Trigger>
        <Portal>
          <Content
            className="z-50 flex max-w-[300px] flex-col gap-1 rounded-lg border border-black bg-black p-3 shadow-lg"
            sideOffset={4}
            onPointerDownOutside={closeTooltip}
            onEscapeKeyDown={closeTooltip}
            collisionPadding={10}
          >
            {label && <p className="text-xs text-white">{label}</p>}
            {description && <p className="text-xs text-white">{description}</p>}
            <Arrow className="fill-black" />
          </Content>
        </Portal>
      </Root>
    </Provider>
  );
};

export default Tooltip;
